import type { InfiniteData } from '@tanstack/react-query';

import type { ChatApiItem, ChatsResponse } from './types';

/** A chats list cache is either a single response (useChats) or paginated (useInfiniteChats). */
export type ChatsCacheData = ChatsResponse | InfiniteData<ChatsResponse>;

function isInfinite(data: ChatsCacheData): data is InfiniteData<ChatsResponse> {
  return Array.isArray((data as InfiniteData<ChatsResponse>).pages);
}

/** Every chat row across both cache shapes, in order. */
export function flattenChats(data: ChatsCacheData | undefined): ChatApiItem[] {
  if (!data) return [];
  return isInfinite(data) ? data.pages.flatMap((p) => p.chats) : data.chats;
}

/**
 * Inject (or update in place) an optimistic chat row. New rows prepend to the
 * first page; an existing id is updated wherever it already lives. Mirrors the
 * flat-cache behaviour the sidebar relied on, but works on the infinite cache too.
 */
export function upsertOptimisticChat(old: ChatsCacheData | undefined, item: ChatApiItem): ChatsCacheData | undefined {
  if (!old) return old;

  const updateExisting = (page: ChatsResponse): ChatsResponse => ({
    ...page,
    chats: page.chats.map((c) => (c.id === item.id ? { ...c, title: item.title, isOptimistic: true } : c)),
  });

  if (!isInfinite(old)) {
    const exists = old.chats.some((c) => c.id === item.id);
    if (exists) return updateExisting(old);
    return { ...old, chats: [item, ...old.chats], total: old.total + 1 };
  }

  const exists = old.pages.some((p) => p.chats.some((c) => c.id === item.id));
  if (exists) return { ...old, pages: old.pages.map(updateExisting) };
  // Prepend to page 0; bump total on every page so `loaded < total` stays exact.
  return {
    ...old,
    pages: old.pages.map((p, i) => ({
      ...p,
      total: p.total + 1,
      chats: i === 0 ? [item, ...p.chats] : p.chats,
    })),
  };
}

/** Apply `update` to the row with `id` in either cache shape; identity when absent. */
function updateChatInCache(
  old: ChatsCacheData | undefined,
  id: string,
  update: (c: ChatApiItem) => ChatApiItem
): ChatsCacheData | undefined {
  if (!old) return old;

  const updatePage = (page: ChatsResponse): ChatsResponse =>
    page.chats.some((c) => c.id === id)
      ? { ...page, chats: page.chats.map((c) => (c.id === id ? update(c) : c)) }
      : page;

  if (!isInfinite(old)) return updatePage(old);
  if (!old.pages.some((p) => p.chats.some((c) => c.id === id))) return old;
  return { ...old, pages: old.pages.map(updatePage) };
}

/**
 * Flag a just-ended session's row as awaiting server title confirmation.
 * The sidebar renders a subtle spinner while set (CLOUD-612).
 */
export function markChatTitlePending(old: ChatsCacheData | undefined, id: string): ChatsCacheData | undefined {
  return updateChatInCache(old, id, (c) => (c.titlePending ? c : { ...c, titlePending: true }));
}

/** Drop the pending flag (title confirmed, or reconciliation gave up). */
export function clearChatTitlePending(old: ChatsCacheData | undefined, id: string): ChatsCacheData | undefined {
  return updateChatInCache(old, id, (c) => (c.titlePending ? { ...c, titlePending: false } : c));
}

/** Remove a chat row from whichever page holds it, decrementing total. */
export function removeChatFromCache(old: ChatsCacheData | undefined, id: string): ChatsCacheData | undefined {
  if (!old) return old;

  if (!isInfinite(old)) {
    const filtered = old.chats.filter((c) => c.id !== id);
    if (filtered.length === old.chats.length) return old;
    return { ...old, chats: filtered, total: Math.max(0, old.total - 1) };
  }

  const found = old.pages.some((p) => p.chats.some((c) => c.id === id));
  if (!found) return old;
  return {
    ...old,
    pages: old.pages.map((p) => ({
      ...p,
      chats: p.chats.filter((c) => c.id !== id),
      total: Math.max(0, p.total - 1),
    })),
  };
}
